import React from 'react';
import { useLocation } from '@reach/router';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import Text from '@components/shared/Text';
import { ImageTextQuadrantData, SliceImageTextQuadrantItem, PageSlice } from '@contracts';
import { useCountry } from '@hooks/useCountry';
import { replaceText } from '@utils/handleTextReplacement';
import { MEDIA } from '@constants';
import ImageTextQuadrantItem from './ImageTextQuadrantItem';

interface Props {
  slice: PageSlice;
}

const ImageTextQuadrant: React.FC<Props> = ({ slice }) => {
  const location = useLocation();
  const { country } = useCountry();
  const primary = slice.primary as ImageTextQuadrantData;
  const items = slice.items as SliceImageTextQuadrantItem[];
  const image = getImage(primary?.main_image);
  const imageAlt = replaceText(primary?.main_image?.alt, country, location) ?? '';
  const headingHTML = primary?.heading?.html;
  const backgroundGradient = primary?.background_gradient?.text || 'none';
  const backgroundColor = primary?.background_color;
  const imageWidth = primary?.image_width?.text ?? '300px';
  const mobileImageWidth = primary?.mobile_image_width?.text ?? '200px';

  return (
    <Container $backgroundGradient={backgroundGradient || 'none'} $backgroundColor={backgroundColor}>
      {headingHTML && (
        <TextContainer>
          <Text text={headingHTML} />
        </TextContainer>
      )}
      <ThreeColumnLayout>
        <Column>
          {items.slice(0, 2).map((item, index) => (
            <ImageTextQuadrantItem key={index} item={item} />
          ))}
        </Column>
        <ImageWrap $imageWidth={imageWidth} $mobileImageWidth={mobileImageWidth}>
          {image && <GatsbyImage image={image} alt={imageAlt} loading="lazy" />}
        </ImageWrap>
        <Column>
          {items.slice(2).map((item, index) => (
            <ImageTextQuadrantItem key={index} item={item} />
          ))}
        </Column>
      </ThreeColumnLayout>
    </Container>
  );
};

export default ImageTextQuadrant;

interface ContainerProps {
  $backgroundGradient?: string;
  $backgroundColor?: string;
}

const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  padding: 90px 30px;
  gap: 46px;
  justify-content: center;
  align-items: center;
  background-color: ${(props) => (props.$backgroundColor ? props.$backgroundColor : '')};
  background-image: ${(props) => (props.$backgroundGradient ? props.$backgroundGradient : 'none')};

  ${MEDIA.tablet} {
    padding: 56px 23px;
  }
`;

const TextContainer = styled.div`
  h3 {
    font-size: 48px;
    line-height: 140%;
    letter-spacing: -0.48px;

    ${MEDIA.tablet} {
      font-size: 32px;
    }
  }
`;

const ThreeColumnLayout = styled.div`
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 3rem;
  max-width: 1200px;
  margin: 0 auto;

  ${MEDIA.tablet} {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const Column = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3rem;
  padding: 16px 0;
  justify-content: space-between;

  ${MEDIA.tablet} {
    padding: 0;
    gap: 16px;
  }
`;

interface ImageWrapProps {
  $imageWidth?: string;
  $mobileImageWidth?: string;
}

const ImageWrap = styled.div<ImageWrapProps>`
  display: flex;
  align-items: center;
  justify-content: center;

  img {
    width: ${(props) => props.$imageWidth} !important;
  }

  ${MEDIA.tablet} {
    margin: 0 auto;
    img {
      width: ${(props) => props.$mobileImageWidth} !important;
    }
  }
`;
